import { useEffect, useMemo, useState } from "react";
import type { CheckoutEvent, TEvent } from "@apac/components/checkout";
import { ProductType } from "@b2bportal/core-types";
import type { SelectTravelerEvent } from "@checkout/states/common/TravelerInformation/events";
import {
  getParentState,
  ParentState,
  TravelerEventTypes,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import type { ITraveler } from "@hopper-b2b/types";
import { ContactAndTravelerSelectors } from "../states";

export const useTravelerSelection = (
  productType?: ProductType,
  singleTravelerWorkflow = true,
  resetErrors = true
) => {
  const [state, send] = useCheckoutState<TEvent, CheckoutEvent>();
  const travelerList = useSelector(ContactAndTravelerSelectors.getTravelers);

  const travelerError = useSelector(
    ContactAndTravelerSelectors.getTravelerError
  );

  const hasError = useSelector(
    ContactAndTravelerSelectors.getTravelerErrorOpen
  );

  const currentParentState = useMemo(
    () => getParentState(state?.value),
    [state?.value]
  );
  // for desktop only
  const hasCompletedTravelerInformationStep =
    currentParentState !== ParentState.contactAndTravelerInformation;

  const openTravelerFormModal = useSelector(
    ContactAndTravelerSelectors.getOpenTravelerFormModal
  );

  const selectedTravelers = useSelector(
    ContactAndTravelerSelectors.getAllSelectedTravelers
  );

  const selectedTravelersIncludingInfants = useSelector(
    ContactAndTravelerSelectors.getAllSelectedTravelersIncludingInfants
  );

  const currentTraveler = useSelector(
    ContactAndTravelerSelectors.getCurrentTraveler
  );
  const currentInfantId = useSelector(
    ContactAndTravelerSelectors.getCurrentInfantTraveler
  );

  const [traveler, setTraveler] = useState<ITraveler | undefined>(
    currentTraveler
  );
  const [isTravelerValid, setTravelerValid] = useState<boolean>(false);

  const existingTraveler = useMemo(
    () => currentTraveler && currentTraveler.id,
    [currentTraveler]
  );

  const isLoading = useSelector(
    ContactAndTravelerSelectors.getIsTravelerInformationLoading
  );

  useEffect(() => {
    if (hasError && resetErrors) {
      send(TravelerEventTypes.CLEAR_TRAVELER_INFORMATION_ERROR);
    }
  }, [hasError, resetErrors, send]);

  const handleSelectTraveler = (travelerId: string) => {
    const traveler = travelerList.find((p) => p.id === travelerId);
    if (traveler == null) return;

    // Flight-specific traveler selection logic
    if (productType === ProductType.FLIGHTS) {
      // Clear any existing errors
      send({
        type: TravelerEventTypes.CLEAR_TRAVELER_INFORMATION_ERROR,
      });

      // Set current traveler for flight context
      send({
        type: TravelerEventTypes.SET_CURRENT_TRAVELER,
        traveler,
      });

      // flight action should be handled differently libs/checkout/src/states/common/TravelerInformation/actions.ts:selectFlightTraveler
      // remember to update the state machine action for it.
      send({
        type: TravelerEventTypes.SELECT_TRAVELER,
        travelerId: traveler.id,
        singleTravelerWorkflow: false,
      } as SelectTravelerEvent);
    } else if (
      // Cars-specific logic
      productType === ProductType.CARS &&
      traveler.groundTravelerDetails?.driverLicense == null
    ) {
      send({
        type: TravelerEventTypes.SET_TRAVELER_ERROR,
        // error content doesn't matter before we introducing more errors
        // For now, CreateOrUpdateTravelerResponse doesn't have a error types defined, we're just checking if there's an error
        data: {
          code: "MISSING_DRIVER_LICENSE",
          message: "Driver license is required",
        },
      });
      send({
        type: TravelerEventTypes.SELECT_TRAVELER,
        travelerId,
        singleTravelerWorkflow,
      } as SelectTravelerEvent);
      send({
        type: TravelerEventTypes.SET_CURRENT_TRAVELER,
        traveler,
      });
      send(TravelerEventTypes.OPEN_TRAVELER_FORM);
    } else if (productType === ProductType.PACKAGE) {
      // Clear any existing errors
      send({
        type: TravelerEventTypes.CLEAR_TRAVELER_INFORMATION_ERROR,
      });

      // Set current traveler for flight context
      send({
        type: TravelerEventTypes.SET_CURRENT_TRAVELER,
        traveler,
      });

      // flight action should be handled differently libs/checkout/src/states/common/TravelerInformation/actions.ts:selectFlightTraveler
      // remember to update the state machine action for it.
      send({
        type: TravelerEventTypes.SELECT_TRAVELER,
        travelerId: traveler.id,
        singleTravelerWorkflow: false,
      } as SelectTravelerEvent);
    } else {
      // Default logic for other product types
      send({
        type: TravelerEventTypes.CLEAR_TRAVELER_INFORMATION_ERROR,
      });
      send({
        type: TravelerEventTypes.SELECT_TRAVELER,
        travelerId,
        singleTravelerWorkflow,
      } as SelectTravelerEvent);
    }
  };

  const handleEditClick = (travelerId: string) => {
    const traveler = travelerList.find((p) => p.id === travelerId);
    if (traveler == null) return;

    send({
      type: TravelerEventTypes.SET_CURRENT_TRAVELER,
      traveler,
    });
    send(TravelerEventTypes.OPEN_TRAVELER_FORM);
  };

  const handleDeleteTraveler = (travelerId) => {
    send({
      type: TravelerEventTypes.DELETE_TRAVELER,
      travelerId: travelerId,
    });
  };

  const handleUpdateTraveler = (newTraveler) => {
    if (newTraveler) {
      send({
        type: TravelerEventTypes.UPDATE_TRAVELER,
        traveler: newTraveler,
        onUpdate: !!existingTraveler,
      });
    }
  };

  const handleClickAddTraveler = () => {
    send({
      type: TravelerEventTypes.SET_CURRENT_TRAVELER,
      traveler: null,
    });
    send(TravelerEventTypes.OPEN_TRAVELER_FORM);
  };

  const handleCloseForm = () => {
    send({ type: TravelerEventTypes.OPEN_TRAVELER_PICKER });
  };

  const handleInfantModalClose = () => {
    send({
      type: TravelerEventTypes.SET_CURRENT_INFANT_ID,
      infantId: null,
    });
  };

  const handleInfantModalContinue = (
    seatType: "OwnSeat" | "OnLap" | "NotTraveling"
  ) => {
    send({
      type: TravelerEventTypes.ON_INFANT_MODAL_CONTINUE,
      seatType,
    });
    handleInfantModalClose();
  };

  return {
    context: {
      travelerList,
      selectedTravelers,
      currentTraveler,
      isTravelerValid,
      traveler,
      isLoading,
      openTravelerFormModal,
      disabled: hasCompletedTravelerInformationStep,
      travelerError,
      openInfantDialog: currentInfantId != null,
      selectedTravelersIncludingInfants,
    },
    handlers: {
      handleSelectTraveler,
      handleEditClick,
      handleDeleteTraveler,
      handleUpdateTraveler,
      handleClickAddTraveler,
      handleCloseForm,
      setTraveler,
      setTravelerValid,
      handleInfantModalClose,
      handleInfantModalContinue,
    },
  };
};
